'use client';

import React, { useState } from 'react';
// import Image from 'next/image'; // REMOVED: Causing 2.9s delays in production
import { Skeleton } from './skeleton';
import { useSignedUrl } from '@/hooks/use-optimized-signed-urls';

interface CoverImageProps {
  // For direct signed URL usage (backward compatibility)
  src?: string | null;
  alt?: string;
  className?: string;
  height?: string;
  sizes?: string;
  priority?: boolean;
  
  // For blob pathname usage (consolidating AsyncCoverImage functionality)
  coverUrl?: string;
  title?: string;
  preloadedUrl?: string;
}

const CoverImage: React.FC<CoverImageProps> = ({
  src,
  alt,
  className = '',
  height = 'h-80',
  sizes = '100vw',
  priority = false,
  
  // New props from AsyncCoverImage
  coverUrl,
  title,
  preloadedUrl,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Handle signed URL fetching for blob pathnames
  const shouldFetchSignedUrl = coverUrl && !preloadedUrl;
  const { signedUrl, isLoading: isLoadingUrl, error: urlError } = useSignedUrl(
    shouldFetchSignedUrl ? coverUrl : null
  );

  // Determine final image source
  let finalSrc: string | null = null;
  if (src) {
    // Direct src prop (backward compatibility)
    finalSrc = src;
  } else if (preloadedUrl) {
    // Use preloaded URL if available
    finalSrc = preloadedUrl;
  } else if (signedUrl) {
    // Use fetched signed URL
    finalSrc = signedUrl;
  }

  // Determine alt text
  const finalAlt = alt || (title ? `${title} cover image` : 'Cover image');

  // Show loading skeleton if fetching URL or image hasn't loaded yet
  if (shouldFetchSignedUrl && isLoadingUrl) {
    return (
      <div className={`w-full ${height} relative overflow-hidden ${className}`}>
        <Skeleton className="absolute inset-0 w-full h-full" />
      </div>
    );
  }

  // Handle URL fetch error or no src
  if (urlError || !finalSrc) {
    return null; // Gracefully hide if image fails to load or no source
  }

  return (
    <div className={`w-full ${height} relative overflow-hidden ${className}`}>
      {!imageLoaded && !imageError && (
        <Skeleton className="absolute inset-0 w-full h-full" />
      )}
      <img
        src={finalSrc}
        alt={finalAlt}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ display: imageError ? 'none' : 'block' }}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageError(true)}
      />
      {imageError && (
        <div className="absolute inset-0 w-full h-full bg-muted flex items-center justify-center">
          <span className="text-muted-foreground text-sm">Failed to load cover image</span>
        </div>
      )}
    </div>
  );
};

export default CoverImage; 